import dayjs, { Dayjs } from "dayjs";
import { readWallTime } from "@/utils/appointmentValidationError";

/**
 * PRET-1917 / design 8b — everything the release-date conflict DECIDES, as pure functions.
 *
 * Separated from `ClientEditModal` because the modal cannot be rendered in this repo's jest without
 * standing up react-hook-form, react-query, i18next, the settings hooks and the MUI X pickers. The
 * logic below is where the defects live — which sentence is shown, whether SAVE is available, how a
 * stored wall-time is read — so it is put where a test can reach it. Same reasoning as the mobile
 * repo's `accountCreatedModel.ts`.
 */

export type ReleaseDateConflict = {
  /** The banner's bold first line. */
  title: string;
  /** The existing appointment restated in full. Absent when the backend sent no `cause`. */
  detail?: string;
  /** True while a replacement first-appointment date is required to save. */
  requiresCorrection: boolean;
};

export const CONFLICT_TITLE =
  "The first appointment is now on or before the release date.";

export const CORRECTION_CALL_TO_ACTION =
  "Set a new first-appointment date to save this change.";

/** 8b element e6 — the footer states why SAVE is unavailable rather than hiding it on hover. */
export const BLOCKED_FOOTER_REASON =
  "Set a new first-appointment date to save.";

/**
 * 8b element e2, line 2 — the existing appointment restated so the coordinator does not have to
 * leave the modal to look it up.
 *
 * Read as WALL TIME. The stored timestamps carry a `Z` they do not mean: `2026-07-25T10:30:00Z` is
 * 10:30 as the participant experiences it. Converting would tell a coordinator in one time zone
 * that an appointment agreed for 10:30 is at 05:30 — the bug this whole convention exists to avoid.
 *
 * Degrades to the call to action alone when the backend named the code but sent no times. The save
 * was still refused, so the coordinator must still be told what to do.
 */
export const buildConflictDetail = (
  start?: string,
  end?: string,
  agencyName?: string
): string => {
  const startAt = readWallTime(start);
  const endAt = readWallTime(end);

  if (!startAt?.isValid() || !endAt?.isValid()) {
    return CORRECTION_CALL_TO_ACTION;
  }

  const when = `${startAt.format("D MMM YYYY")} · ${startAt.format(
    "HH:mm"
  )} – ${endAt.format("HH:mm")}`;

  return `First appointment: ${when}${
    agencyName ? ` at ${agencyName}` : ""
  }. ${CORRECTION_CALL_TO_ACTION}`;
};

/**
 * 8b element e3 — the correction section's helper.
 *
 * Interpolates the NEW release date, and states that the agency and location do not change, which
 * is why the section offers no pickers for them.
 */
export const buildCorrectionHelper = (
  releaseDate?: Date | string | Dayjs | null
): string => {
  const when = releaseDate ? dayjs(releaseDate) : null;

  return `Must be after ${
    when?.isValid() ? when.format("D MMMM YYYY") : "the release date"
  }. Agency and location are unchanged.`;
};

/**
 * Whether a replacement date clears the rule the release date broke.
 *
 * Eligibility begins the DAY AFTER release — a participant is in custody until then — so the
 * release date itself and every earlier date are invalid, not just the release date. Same boundary
 * as the invite modal's yup test and the server's `isAfter(releaseDay, "day")`. Enforced here
 * because the edit schema deliberately leaves these fields optional: the first appointment already
 * exists and is not normally asked for again.
 */
export const isCorrectionAfterRelease = (
  correctionDate?: Date | string | Dayjs | null,
  releaseDate?: Date | string | Dayjs | null
): boolean => {
  if (!correctionDate || !releaseDate) return false;

  const correction = dayjs(correctionDate);
  const release = dayjs(releaseDate);
  if (!correction.isValid() || !release.isValid()) return false;

  return correction.startOf("day").isAfter(release.startOf("day"));
};

export type CorrectionValues = {
  date?: Date | string | Dayjs | null;
  start?: Date | string | Dayjs | null;
  end?: Date | string | Dayjs | null;
};

/** All three fields supplied AND the date actually clears the release date. */
export const isCorrectionComplete = (
  correction: CorrectionValues,
  releaseDate?: Date | string | Dayjs | null
): boolean =>
  Boolean(
    correction.date &&
    correction.start &&
    correction.end &&
    isCorrectionAfterRelease(correction.date, releaseDate)
  );

/**
 * 8b element e6 — SAVE is unavailable only while a correction is being asked for and is not yet
 * usable. An ordinary edit, and a release-date move that leaves the appointment valid (AC3), are
 * both unaffected.
 */
export const isBlockedByConflict = (
  conflict: ReleaseDateConflict | null,
  correction: CorrectionValues,
  releaseDate?: Date | string | Dayjs | null
): boolean =>
  Boolean(conflict?.requiresCorrection) &&
  !isCorrectionComplete(correction, releaseDate);
